import { ProdutoProducaoEditor } from "./commerce/ProdutoProducaoEditor";
import { ProdutoFinanceiro } from "./commerce/ProdutoFinanceiro";
import { prepararProducaoProduto, analisarProdutoEditado, erroProducaoProduto, patchProducaoProduto } from "../utils/producaoProduto";
import { CommerceDialog, CommerceTabs, CommercePanel } from "./commerce/CommerceDialog";
import { useState, useEffect, useRef } from "react";
import { toast } from "../hooks/useToast";
import { SkeletonCard } from "./ui/Skeleton";
import {C, withAlpha } from "../constants/theme";
import { fbList, fbAdd, fbUpdate, fbDelete } from "../hooks/useFirebase";
import { useConfirm } from "../hooks/useConfirm";
import { getUser } from "../utils/sessao";
import { storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { arrayUnion, arrayRemove } from "firebase/firestore";
import { Card } from "./ui/Card";
import { Inp } from "./ui/Inp";
import { Btn } from "./ui/Btn";
import { Badge } from "./ui/Badge";
import { FotoUploader } from "./ui/FotoUploader";
import { ImportarFotosMakerWorld } from "./makerworld/ImportarFotosMakerWorld";
import { LinkProdutoMakerWorld } from "./makerworld/LinkProdutoMakerWorld";
import { resizeParaBlob } from "../utils/extract3mf";
import { temArquivo3mfValido } from "../utils/arquivos3d";
import { garantirFotoPrincipal } from "../utils/fotosProduto";
import { categoriasDoProduto, emojiDaCategoria, categoriasAutomaticasDoProduto } from "../utils/categoriasProduto";
import { useAvisoAoSair } from "../hooks/useAvisoAoSair";
import { ArquivoCard } from "./ArquivoCard";
import { Visualizador3D } from "./Visualizador3D";
import { visualizacao3dDoItem } from "../utils/visualizacao3d";

const CATEGORIAS_INICIAIS = ["Chaveiro", "Espadas", "Armas", "Decoração"];

// Lapisinho que só aparece no hover do número do estoque (ver classe
// .estoque-editavel) — avisa que dá pra clicar e digitar, sem precisar de
// um aviso fixo na tela.
const LapisEstoque = () => (
  <svg className="lapis" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

export function Produtos({ coresCustom = [] }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [categorias, setCategorias] = useState([]);
  const [filtroCat, setFiltroCat] = useState("todas");
  const [novaCat, setNovaCat] = useState("");
  const [gerenciarCat, setGerenciarCat] = useState(false);
  const categorizandoRef = useRef(new Set());
  const [confirmar, modalConfirm] = useConfirm();
  const [detalheId, setDetalheId] = useState(null);
  const [editForm, setEditForm] = useState({ nome:"", preco:"", fotoBase64:"", categorias:[], fotos:[], linkMakerworld:"" });
  const [categoriasEditadas, setCategoriasEditadas] = useState(false);
  const [impressoras, setImpressoras] = useState([]);
  const [filamentos, setFilamentos] = useState([]);
  const [producao, setProducao] = useState(null);
  const [carregandoInsumos, setCarregandoInsumos] = useState(true);
  const [salvandoProduto, setSalvandoProduto] = useState(false);
  const [enviandoFotos, setEnviandoFotos] = useState(false);
  const [editandoEstoqueId, setEditandoEstoqueId] = useState(null); // id do produto com o campo de estoque em edição por digitação
  const [valorEstoqueEdit, setValorEstoqueEdit] = useState("");
  useAvisoAoSair(enviandoFotos || salvandoProduto);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const carregar = async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    const lista = await fbList("produtos");
    setData(lista);
    if (!silencioso) setLoading(false);
    return lista;
  };
  // Confere a lista inteira uma vez ao abrir a tela — produto que ficou sem
  // foto principal (ex.: algum problema no meio de um upload) mas tem de
  // onde tirar uma (galeria já preenchida ou .3mf anexado) é consertado
  // sozinho e silenciosamente, sem precisar o usuário abrir e mexer nele.
  const repararFotosPrincipais = async (lista) => {
    for (const p of lista) {
      const patch = await garantirFotoPrincipal(p).catch(() => null);
      if (!patch) continue;
      try {
        await fbUpdate("produtos", p.id, patch);
        setData(d => d.map(x => x.id === p.id ? { ...x, ...patch } : x));
      } catch { /* silencioso — tenta de novo na próxima vez que a tela abrir */ }
    }
  };
  // Recarregar a lista (ex.: depois do ArquivoCard subir um .3mf e adicionar o
  // thumbnail na galeria) não mexe no editForm sozinho — resincroniza só as
  // fotos/miniatura do produto aberto, sem tocar nome/preço/categoria em edição.
  useEffect(() => {
    if (!detalheId) return;
    const p = data.find(x => x.id === detalheId);
    if (p) setEditForm(f => ({ ...f, fotos: p.fotos || [], fotoBase64: p.fotoBase64 || "" }));
  }, [data, detalheId]); // eslint-disable-line react-hooks/exhaustive-deps
  const carregarCategorias = async () => {
    let cats = await fbList("categorias");
    // Semeia as categorias iniciais uma única vez (respeita exclusões feitas depois)
    const u = getUser();
    const seedKey = `cats_seed_${u?.uid || "x"}`;
    if (cats.length === 0 && !localStorage.getItem(seedKey)) {
      try {
        for (const nome of CATEGORIAS_INICIAIS) await fbAdd("categorias", { nome });
        localStorage.setItem(seedKey, "1");
        cats = await fbList("categorias");
      } catch (e) { console.error("Erro ao semear categorias:", e); }
    }
    setCategorias([...cats].sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "pt-BR", { sensitivity:"base" })));
  };
  useEffect(() => {
    carregar().then(repararFotosPrincipais);
    carregarCategorias();
    Promise.all([fbList("impressoras"), fbList("filamentos")]).then(([imps, fils]) => {
      setImpressoras(imps); setFilamentos(fils);
    }).catch(() => toast("Não foi possível carregar os filamentos e as impressoras.", "error"))
      .finally(() => setCarregandoInsumos(false));
  }, []);
  useEffect(() => {
    categorizandoRef.current.clear();
  }, [categorias]);
  useEffect(() => {
    if (loading || categorias.length === 0) return;
    for (const produto of data) {
      if (produto.categoria_manual || produto.id === detalheId || categorizandoRef.current.has(produto.id)) continue;
      const atuais = categoriasDoProduto(produto);
      const proximas = categoriasAutomaticasDoProduto(produto, categorias);
      if (proximas.length === atuais.length) continue;
      categorizandoRef.current.add(produto.id);
      const patch = { categorias:proximas, categoria:proximas[0], categoria_auto:true };
      fbUpdate("produtos", produto.id, patch)
        .then(() => setData(atual => atual.map(p => p.id === produto.id ? { ...p, ...patch } : p)))
        .catch(e => console.warn("Nao foi possivel categorizar produto:", e));
    }
  }, [data, categorias, loading, detalheId]);

  useEffect(() => {
    if (carregandoInsumos || !detalheId) return;
    const produto = data.find(p => p.id === detalheId);
    if (produto) setProducao(atual => atual && !atual.alterada ? prepararProducaoProduto(produto, impressoras) : atual);
  }, [carregandoInsumos, detalheId, data, impressoras]);

  const adicionarCategoria = async () => {
    const nome = novaCat.trim();
    if (!nome) return;
    if (categorias.some(c => (c.nome || "").toLowerCase() === nome.toLowerCase())) { toast("Categoria já existe", "warning"); setNovaCat(""); return; }
    await fbAdd("categorias", { nome });
    setNovaCat("");
    carregarCategorias();
    toast("Categoria adicionada ✓", "success");
  };
  const removerCategoria = async (cat) => {
    if (await confirmar(`Remover a categoria "${cat.nome}"? Os produtos não serão apagados, apenas ficam sem categoria.`)) {
      await fbDelete("categorias", cat.id);
      if (filtroCat === cat.nome) setFiltroCat("todas");
      carregarCategorias();
    }
  };

  // Toda mudança de estoque de peças prontas feita AQUI (produto entrando ou
  // saindo direto pelo cadastro, não venda) mexe no filamento junto — entrar
  // unidades desconta o que aquela leva consumiu, tirar unidades devolve.
  // deltaUnidades positivo = entrou, negativo = saiu. Usa a "receita" salva
  // no produto (filamentos_usados: peso por peça, gramas). Vendas pelo PDV
  // (Caixa.jsx) NÃO passam por aqui de propósito — o estoque cai lá mas o
  // filamento não é mexido, porque a baixa de material já foi feita quando a
  // peça ENTROU em estoque, na produção; refazer na venda ia descontar 2x.
  const ajustarFilamentoProducao = async (produto, deltaUnidades) => {
    const receita = produto?.filamentos_usados || [];
    if (!receita.length || !deltaUnidades) return;
    const filSnap = await fbList("filamentos");
    const upd = [];
    const novos = {};
    for (const f of receita) {
      if (!f.id) continue;
      const atual = filSnap.find(x => x.id === f.id);
      const consumo = (parseFloat(f.peso) || 0) * deltaUnidades;
      if (!atual || consumo === 0) continue;
      novos[f.id] = Math.max(0, (parseFloat(atual.peso_restante) || 0) - consumo);
      upd.push(fbUpdate("filamentos", f.id, { peso_restante: novos[f.id] }));
    }
    if (upd.length) {
      await Promise.all(upd);
      setFilamentos(fs => fs.map(x => x.id in novos ? { ...x, peso_restante: novos[x.id] } : x));
    }
  };

  const ajustarEstoque = async (id, delta) => {
    const prod = data.find(p => p.id === id);
    if (!prod) return;
    const atual = parseInt(prod.estoque) || 0;
    const novo = Math.max(0, atual + delta);
    await fbUpdate("produtos", id, { estoque: novo });
    setData(d => d.map(p => p.id === id ? { ...p, estoque: novo } : p));
    await ajustarFilamentoProducao(prod, novo - atual);
  };

  // Define o estoque digitando um valor absoluto — atalho pra quando o
  // ajuste é grande (ex: recontagem), sem precisar clicar +/- várias vezes.
  const definirEstoque = async (id, valor) => {
    const prod = data.find(p => p.id === id);
    const atual = parseInt(prod?.estoque) || 0;
    const novo = Math.max(0, parseInt(valor) || 0);
    await fbUpdate("produtos", id, { estoque: novo });
    setData(d => d.map(p => p.id === id ? { ...p, estoque: novo } : p));
    setEditandoEstoqueId(null);
    if (prod) await ajustarFilamentoProducao(prod, novo - atual);
  };
  const abrirEdicaoEstoque = (p) => { setEditandoEstoqueId(p.id); setValorEstoqueEdit(String(parseInt(p.estoque) || 0)); };
  const inputEstoqueStyle = (compact) => ({
    width: compact ? 30 : 46, textAlign:"center", background:C.panel, border:"1px solid "+C.accent2,
    borderRadius:6, color:C.text, fontWeight:900, fontSize: compact ? 12 : 16, outline:"none",
    padding: compact ? "1px 0" : "2px 0", MozAppearance:"textfield",
  });

  const del = async (id) => {
    if (!await confirmar("Excluir produto?")) return;
    const prod = data.find(p => p.id === id);
    // Apaga também os arquivos anexados (3MF/fotos) no Storage — senão ficam
    // órfãos lá pra sempre, sem nenhum produto referenciando eles.
    for (const arq of prod?.arquivos || []) {
      if (arq.path) await deleteObject(ref(storage, arq.path)).catch(() => {});
    }
    for (const foto of prod?.fotos || []) {
      if (foto.path) await deleteObject(ref(storage, foto.path)).catch(() => {});
    }
    await fbDelete("produtos", id);
    setData(d => d.filter(p => p.id !== id));
    setDetalheId(null);
  };

  const abrirDetalhe = (p) => {
    setCategoriasEditadas(false);
    setDetalheId(p.id);
    setProducao(prepararProducaoProduto(p, impressoras));
    setEditForm({ nome: p.nome || "", preco: p.preco ?? "", fotoBase64: p.fotoBase64 || "", categorias: categoriasDoProduto(p), fotos: p.fotos || [], linkMakerworld: p.link_makerworld || "" });
  };

  const salvarEdicao = async () => {
    if (salvandoProduto || !editForm.nome.trim()) return;
    const linkMakerworld = (editForm.linkMakerworld || "").trim();
    if (linkMakerworld && !/^https?:\/\/\S+$/i.test(linkMakerworld)) { toast("O link do MakerWorld precisa começar com https://", "warning"); return; }
    setSalvandoProduto(true);
    try {
      const produto = data.find(p => p.id === detalheId);
      const patch = {
        ...patchProducaoProduto(produto, producao, editForm.preco, impressoras, coresCustom),
        nome: editForm.nome.trim(), categorias: editForm.categorias,
        link_makerworld: linkMakerworld || null,
        categoria: editForm.categorias[0] || null,
        ...(categoriasEditadas ? { categoria_manual:true } : {}),
      };
      await fbUpdate("produtos", detalheId, patch);
      setData(d => d.map(p => p.id === detalheId ? { ...p, ...patch } : p));
      categorizandoRef.current.delete(detalheId);
      toast("Produto salvo ✓", "success");
      setDetalheId(null);
    } catch (e) {
      toast("Não foi possível salvar o produto: " + e.message, "error");
    } finally { setSalvandoProduto(false); }
  };

  // "Miniatura" (fotoBase64) usada por Caixa/Pedidos/Fila/Site/loja pública —
  // fica sincronizada sozinha com a 1ª foto (não-vídeo) da galeria toda vez
  // que ela muda, sem precisar mexer nesses 8 lugares (todos usam <img>, não
  // dá pra apontar pra um vídeo ali). Antes baixava a foto do Storage de
  // volta pra gerar uma cópia comprimida, mas isso depende de CORS estar
  // liberado no bucket (não está, por padrão) e falhava silenciosamente,
  // deixando o produto sem foto principal. Usar a própria URL da foto resolve
  // isso de vez — <img src> carrega de qualquer origem sem precisar de CORS,
  // só o fetch() pra LER os bytes é que precisa.
  const fotoPrincipalDaGaleria = (fotos) => (fotos.find(f => f.tipo !== "video") || fotos[0])?.url || null;

  // Fotos/vídeos de produto vão pro Storage (não base64 no Firestore) — evita
  // estourar o limite de 1MB do documento com várias fotos em alta resolução,
  // e permite abrir em tela cheia/zoom sem ficar borrado. Salva na hora (não
  // espera o botão "Salvar" geral), igual ao padrão de ArquivoCard.jsx.
  const adicionarFotos = async (files) => {
    setEnviandoFotos(true);
    try {
      // Deriva do produto salvo (`data`), não do `editForm` local — mais
      // confiável que o estado da tela pra saber se a galeria já tem algo.
      // A foto antiga (fotoBase64, geralmente o thumbnail do .3mf) NUNCA
      // entra no carretel — ela só serve de capa até a 1ª foto de verdade
      // chegar, e essa 1ª foto passa a ser a nova capa (ver mais abaixo).
      const prodAtual = data.find(p => p.id === detalheId);
      const fotosAtuais = prodAtual?.fotos || editForm.fotos || [];
      const semFotoAntes = fotosAtuais.length === 0;
      const novas = [];
      // "i" no nome do arquivo evita colisão de path quando vários arquivos
      // são enviados juntos rápido demais pro Date.now() sozinho diferenciar
      // (acontecia sobretudo com nomes repetidos, ex.: imagem colada do
      // clipboard sempre se chama "image.png") — sem isso, o upload seguinte
      // sobrescrevia o arquivo do anterior no Storage e a galeria ficava com
      // duas entradas apontando pro mesmo arquivo (uma "sumia"/corrompia).
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const ehVideo = file.type.startsWith("video/");
        // Vídeo e GIF não passam pelo canvas: canvas só captura 1 frame (perderia
        // a animação) e não decodifica vídeo de jeito nenhum — sobem como estão.
        const blob = (ehVideo || file.type === "image/gif") ? file : await resizeParaBlob(file);
        if (!blob) { toast(`Não consegui processar "${file.name}".`, "warning"); continue; }
        const path = `produtos/${detalheId}/${Date.now()}_${i}_${file.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, blob);
        const url = await getDownloadURL(storageRef);
        novas.push(ehVideo ? { path, url, tipo:"video" } : { path, url });
      }
      if (novas.length === 0) { setEnviandoFotos(false); return; }
      // arrayUnion é atômico no servidor — se o usuário adicionar fotos em
      // duas levas quase juntas (2 uploads em paralelo), cada escrita só
      // ACRESCENTA as suas próprias fotos, nunca sobrescreve o array inteiro
      // com uma versão desatualizada (era isso que às vezes "apagava" uma
      // foto recém-adicionada: a 2ª escrita partia de um retrato do array
      // já obsoleto no momento em que o 1º upload ainda não tinha terminado).
      const patch = { fotos: arrayUnion(...novas) };
      // 1ª foto de verdade na galeria substitui a capa (que até então podia
      // ser só o thumbnail do .3mf, nunca presente no carretel) pela própria foto nova.
      if (semFotoAntes) patch.fotoBase64 = fotoPrincipalDaGaleria(novas);
      await fbUpdate("produtos", detalheId, patch);
      const acrescentar = (atuais) => [...atuais, ...novas.filter(n => !atuais.some(x => x.path === n.path))];
      setEditForm(f => ({ ...f, fotos: acrescentar(f.fotos), fotoBase64: semFotoAntes ? (patch.fotoBase64 || "") : f.fotoBase64 }));
      setData(d => d.map(p => p.id === detalheId ? { ...p, fotos: acrescentar(p.fotos || []), ...(semFotoAntes ? { fotoBase64: patch.fotoBase64 } : {}) } : p));
      toast(novas.length > 1 ? `${novas.length} fotos adicionadas ✓` : "Foto adicionada ✓", "success");
    } catch (e) { toast("Erro ao enviar foto: " + e.message, "error"); }
    setEnviandoFotos(false);
  };

  // Fotos importadas do MakerWorld já chegam gravadas no Storage pelo backend
  // ({path,url}) — aqui só entram no produto, igual às enviadas à mão (arrayUnion
  // + 1ª foto vira capa).
  const registrarFotosImportadas = async (novas, { url } = {}) => {
    setEnviandoFotos(true);
    try {
      const prodAtual = data.find(p => p.id === detalheId);
      const semFotoAntes = (prodAtual?.fotos || editForm.fotos || []).length === 0;
      const patch = { fotos: arrayUnion(...novas) };
      if (semFotoAntes) patch.fotoBase64 = fotoPrincipalDaGaleria(novas);
      // Guarda de onde as fotos vieram (sem sobrescrever um link que já esteja no produto).
      const guardarLink = !!url && !prodAtual?.link_makerworld;
      if (guardarLink) patch.link_makerworld = url;
      await fbUpdate("produtos", detalheId, patch);
      const acrescentar = (atuais) => [...atuais, ...novas.filter(n => !atuais.some(x => x.path === n.path))];
      setEditForm(f => ({ ...f, fotos: acrescentar(f.fotos), fotoBase64: semFotoAntes ? (patch.fotoBase64 || "") : f.fotoBase64, ...(guardarLink && !f.linkMakerworld ? { linkMakerworld: url } : {}) }));
      setData(d => d.map(p => p.id === detalheId ? { ...p, fotos: acrescentar(p.fotos || []), ...(semFotoAntes ? { fotoBase64: patch.fotoBase64 } : {}), ...(guardarLink ? { link_makerworld: url } : {}) } : p));
    } finally { setEnviandoFotos(false); }
  };

  const removerFoto = async (path) => {
    setEnviandoFotos(true);
    try {
      const alvo = editForm.fotos.find(f => f.path === path);
      if (!alvo) { setEnviandoFotos(false); return; }
      await deleteObject(ref(storage, path)).catch(() => {});
      const restantes = editForm.fotos.filter(f => f.path !== path);
      // arrayRemove também é atômico — só tira essa foto específica, não
      // depende de reescrever o array inteiro por cima do que estiver lá.
      const patch = { fotos: arrayRemove(alvo) };
      const novaFotoBase64 = fotoPrincipalDaGaleria(restantes);
      if (novaFotoBase64 !== editForm.fotoBase64) patch.fotoBase64 = novaFotoBase64;
      await fbUpdate("produtos", detalheId, patch);
      setEditForm(f => ({ ...f, fotos: f.fotos.filter(x => x.path !== path), fotoBase64: patch.fotoBase64 !== undefined ? (patch.fotoBase64 || "") : f.fotoBase64 }));
      setData(d => d.map(p => p.id === detalheId ? { ...p, fotos: (p.fotos || []).filter(x => x.path !== path), ...(patch.fotoBase64 !== undefined ? { fotoBase64: patch.fotoBase64 } : {}) } : p));
    } catch (e) { toast("Erro ao remover foto: " + e.message, "error"); }
    setEnviandoFotos(false);
  };

  // Reordena a galeria (arrastar uma miniatura sobre outra) — ids na nova
  // ordem. A 1ª vira a principal (fotoBase64), igual ao add/remove.
  const reordenarFotos = async (ids) => {
    const novoArray = ids.map(id => editForm.fotos.find(f => f.path === id)).filter(Boolean);
    if (novoArray.length !== editForm.fotos.length) return; // segurança: algo não bateu, não salva
    const novaFotoBase64 = fotoPrincipalDaGaleria(novoArray);
    const patch = { fotos: novoArray, fotoBase64: novaFotoBase64 };
    try {
      await fbUpdate("produtos", detalheId, patch);
      setEditForm(f => ({ ...f, fotos: novoArray, fotoBase64: novaFotoBase64 || "" }));
      setData(d => d.map(p => p.id === detalheId ? { ...p, ...patch } : p));
    } catch (e) { toast("Erro ao reordenar fotos: " + e.message, "error"); }
  };

  if (loading) return (
    <div style={{ display:"grid", gridTemplateColumns:isMobile?"repeat(3,minmax(0,1fr))":"repeat(auto-fill,minmax(200px,1fr))", gap:isMobile?8:12 }}>
      {[...Array(8)].map((_,i) => <SkeletonCard key={i} rows={2} />)}
    </div>
  );

  const dadosFiltrados = data.filter(p =>
    (!busca || p.nome?.toLowerCase().includes(busca.toLowerCase())) &&
    (filtroCat === "todas" || (filtroCat === "sem" ? categoriasDoProduto(p).length===0 : categoriasDoProduto(p).includes(filtroCat)))
  );
  const detalhe = data.find(p => p.id === detalheId);
  const resultadoProduto = detalhe && producao ? analisarProdutoEditado(detalhe, producao, editForm.preco, impressoras) : null;
  const erroEdicao = producao ? erroProducaoProduto(producao, editForm.preco) : "";

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <style>{`
        /* Estoque clicável: lapisinho só aparece no hover, avisa sem poluir
           a tela — igual já foi usado pra decidir isso num teste visual. */
        .estoque-editavel { display:inline-flex; align-items:center; gap:4px; padding:3px 6px; border-radius:7px; transition:background .15s ease; }
        .estoque-editavel:hover { background:${C.panel}; }
        .estoque-editavel .lapis { width:10px; height:10px; opacity:0; transform:translateX(-2px); transition:opacity .15s ease, transform .15s ease; color:${C.accent2}; flex-shrink:0; }
        .estoque-editavel:hover .lapis { opacity:1; transform:translateX(0); }
      `}</style>
      {modalConfirm}

      {data.length === 0 ? (
        <Card>
          <div style={{ textAlign:"center", padding:40, color:C.muted }}>
            <div style={{ fontSize:48, marginBottom:12 }}>📦</div>
            <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:6 }}>Nenhum produto registrado</div>
            <div style={{ fontSize:13 }}>Use a <strong style={{ color:C.warning }}>Calculadora</strong> para calcular o custo e registrar um produto.</div>
          </div>
        </Card>
      ) : (
        <div style={{ position:"relative" }}>
          <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", fontSize:14, pointerEvents:"none" }}>{"\uD83D\uDD0D"}</span>
          <input
              value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder="Pesquisar produto..."
              style={{ width:"100%", boxSizing:"border-box", background:"var(--pc-ui-0a1628, #0a1628)", border:"1px solid var(--pc-white-8, rgba(255,255,255,0.08))", borderRadius:10, color:"var(--pc-ui-f1f5f9, #f1f5f9)", fontSize:13, padding:"10px 36px 10px 38px", outline:"none" }}
          />
          {busca && (
              <button onClick={() => setBusca("")} aria-label="Limpar pesquisa" style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"#64748b", cursor:"pointer", fontSize:15, lineHeight:1, padding:0 }}>{"\u2715"}</button>
          )}
        </div>
      )}

      <div style={{ display:"flex", alignItems:"flex-start", gap:isMobile?8:16, minWidth:0 }}>
      {data.length > 0 && (
        <aside aria-label="Categorias" style={{ width:isMobile?104:180, flexShrink:0, background:C.panel, border:"1px solid "+C.border, borderRadius:12, padding:isMobile?6:10, boxSizing:"border-box", maxHeight:"70vh", overflowY:"auto" }}>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {/* Gerenciar fica no topo, separado dos filtros por uma linha. */}
          <button onClick={()=>setGerenciarCat(v=>!v)} aria-expanded={gerenciarCat}
            style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, width:"100%", background:gerenciarCat?"rgba(0,212,170,0.15)":C.card, border:"1px dashed "+(gerenciarCat?C.accent2:C.border), borderRadius:8, padding:isMobile?"7px 6px":"8px 10px", fontSize:12, fontWeight:700, cursor:"pointer", color:gerenciarCat?C.accent2:C.text }}>
            {gerenciarCat ? "✕ Fechar" : isMobile ? "⚙️ Editar" : "⚙️ Editar categorias"}
          </button>
          {gerenciarCat && (
            <div style={{ background:C.card, border:"1px solid "+C.border, borderRadius:10, padding:10, display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                <input value={novaCat} onChange={e=>setNovaCat(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter") adicionarCategoria(); }} placeholder="Nova categoria..."
                  style={{ width:"100%", boxSizing:"border-box", background:C.panel, border:"1px solid "+C.border, borderRadius:8, color:C.text, padding:"8px 12px", fontSize:13, outline:"none" }} />
                <Btn small color={C.accent2} onClick={adicionarCategoria}>➕ Adicionar</Btn>
              </div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {categorias.length===0 && <span style={{ color:C.muted, fontSize:12 }}>Nenhuma categoria. Adicione acima.</span>}
                {categorias.map(c=>(
                  <span key={c.id} style={{ display:"flex", alignItems:"center", gap:6, background:C.panel, border:"1px solid "+C.border, borderRadius:20, padding:"4px 6px 4px 12px", fontSize:12, color:C.text }}>
                    {emojiDaCategoria(c.nome)} {c.nome}
                    <button onClick={()=>removerCategoria(c)} title="Remover categoria" style={{ background:"none", border:"none", color:C.danger, cursor:"pointer", fontSize:13, lineHeight:1, padding:"2px 4px" }}>✕</button>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div style={{ height:1, background:C.border }} />
          <div style={{ display:"flex", flexDirection:"column", gap:8, minWidth:0 }}>
            <div style={{ display:"flex", flexDirection:"column", gap:6, minWidth:0 }}>
              {(() => {
                const semCat = data.filter(p=>categoriasDoProduto(p).length===0).length;
                const chips = [
                  { id:"todas", label:"Todas" },
                  ...categorias.map(c=>({ id:c.nome, label:c.nome })),
                  ...(semCat>0 ? [{ id:"sem", label:"Sem categoria" }] : []),
                ];
                return chips.map(ch => {
                  const ativo = filtroCat===ch.id;
                  return (
                    <button key={ch.id} onClick={()=>setFiltroCat(ch.id)}
                      style={{ background:ativo?"rgba(0,212,170,0.15)":"transparent", color:ativo?C.accent2:C.muted, border:"1px solid "+(ativo?C.accent2:C.border), borderRadius:8, padding:isMobile?"7px 8px":"8px 10px", cursor:"pointer", fontWeight:ativo?700:500, fontSize:12, textAlign:"left", width:"100%", overflowWrap:"anywhere" }}>
                      {ch.id === "todas" ? "\uD83C\uDFF7\uFE0F Todas" : ch.id === "sem" ? "\uD83C\uDFF7\uFE0F Sem categoria" : emojiDaCategoria(ch.label) + " " + ch.label}
                    </button>
                  );
                });
              })()}
            </div>
          </div>
        </div>
        </aside>
      )}

      {/* Grade: só foto + estoque — o resto fica no popup de detalhe.
          No mobile, 3 colunas fixas (em vez de minmax(200px,...), que só
          cabia 1 por linha) — cards mais compactos: foto mais baixa,
          botões de estoque menores e mais próximos pra não vazar. */}
      <div style={{ flex:1, minWidth:0 }}>
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"repeat(auto-fill,minmax(min(100%,140px),1fr))":"repeat(auto-fill,minmax(200px,1fr))", gap:isMobile?8:12 }}>
        {dadosFiltrados.length === 0 && (busca || filtroCat !== "todas") && (
          <div style={{ gridColumn:"1/-1", textAlign:"center", color:"#64748b", padding:32, fontSize:13 }}>
            Nenhum produto encontrado{busca ? ` para "${busca}"` : " nesta categoria"}
          </div>
        )}
        {dadosFiltrados.map(p => {
          const estoque = parseInt(p.estoque) || 0;
          const sem3mf = !temArquivo3mfValido(p.arquivos);
          // Sem foto = sem capa E sem nenhuma foto (vídeo não conta) na galeria.
          const semFoto = !p.fotoBase64 && !(p.fotos || []).some(f => f.tipo !== "video");
          const seloAviso = { display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(10,22,40,0.85)", border:"1px solid "+withAlpha(C.danger, "88"), borderRadius:20, padding:isMobile?"3px 5px":"4px 6px", fontSize:isMobile?10:12, lineHeight:1 };
          return (
            <div key={p.id} onClick={() => abrirDetalhe(p)}
              style={{ background:C.card, border:"1px solid "+C.border, borderRadius:isMobile?10:12, overflow:"hidden", cursor:"pointer" }}>
              <div style={{ position:"relative", width:"100%", height:isMobile?76:120, background:"var(--pc-ui-0a1628, #0a1628)", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
                {p.fotoBase64
                  ? <img src={p.fotoBase64} alt={p.nome} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                  : <div style={{ fontSize:isMobile?20:30, opacity:0.3 }}>📦</div>}
                {p.impressora_nome && (
                  <div title={"Calculado com: "+p.impressora_nome}
                    style={{ position:"absolute", top:6, left:6, display:"flex", alignItems:"center", gap:3, background:"rgba(10,22,40,0.85)", border:"1px solid "+withAlpha(C.accent2, "66"), borderRadius:20, padding:isMobile?"2px 6px":"3px 8px", fontSize:isMobile?8.5:10, fontWeight:800, color:C.accent2, maxWidth:"75%", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    🖨️ {p.impressora_nome}
                  </div>
                )}
                {(sem3mf || semFoto) && (
                  <div style={{ position:"absolute", top:isMobile?6:36, right:6, display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
                    {sem3mf && <div title="Sem arquivo 3mf" style={seloAviso}>📂❌</div>}
                    {semFoto && <div title="Sem foto" style={seloAviso}>🖼️❌</div>}
                  </div>
                )}
                {!isMobile && (
                  <button onClick={e=>{ e.stopPropagation(); abrirDetalhe(p); }} title="Ver detalhes do produto"
                    style={{ position:"absolute", top:6, right:6, width:26, height:26, borderRadius:"50%", background:"rgba(10,22,40,0.85)", border:"1px solid "+C.accent2, color:C.accent2, fontSize:14, fontWeight:900, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1, padding:0 }}>
                    ℹ️
                  </button>
                )}
                <div style={{ position:"absolute", bottom:0, left:0, right:0, background:"linear-gradient(0deg,rgba(0,0,0,0.6),transparent)", color:"#fff", fontSize:isMobile?9.5:11, fontWeight:700, padding:isMobile?"10px 5px 4px":"14px 8px 5px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {p.nome}
                </div>
              </div>
              <div style={{ padding:isMobile?"5px 4px":"8px 10px", display:"flex", alignItems:"center", justifyContent:"center", gap:isMobile?4:10 }}>
                <button onClick={e=>{ e.stopPropagation(); ajustarEstoque(p.id,-1); }}
                  style={{ width:isMobile?20:26, height:isMobile?20:26, border:"1px solid "+C.border, background:C.panel, color:C.danger, borderRadius:6, cursor:"pointer", fontSize:isMobile?12:15, fontWeight:900, display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1, flexShrink:0 }}>
                  −
                </button>
                {editandoEstoqueId === p.id ? (
                  <input
                    type="number" autoFocus value={valorEstoqueEdit}
                    onClick={e=>e.stopPropagation()}
                    onChange={e=>setValorEstoqueEdit(e.target.value)}
                    onBlur={()=>definirEstoque(p.id, valorEstoqueEdit)}
                    onKeyDown={e=>{ if(e.key==="Enter") e.currentTarget.blur(); if(e.key==="Escape") setEditandoEstoqueId(null); }}
                    style={inputEstoqueStyle(isMobile)}
                  />
                ) : (
                  <span className="estoque-editavel" onClick={e=>{ e.stopPropagation(); abrirEdicaoEstoque(p); }} title="Clique pra digitar o estoque"
                    style={{ color: estoque>0?C.text:C.danger, fontWeight:900, fontSize:isMobile?12:15, minWidth:isMobile?14:22, textAlign:"center", cursor:"pointer" }}>{estoque}<LapisEstoque /></span>
                )}
                <button onClick={e=>{ e.stopPropagation(); ajustarEstoque(p.id,+1); }}
                  style={{ width:isMobile?20:26, height:isMobile?20:26, border:"1px solid "+C.border, background:C.panel, color:C.success, borderRadius:6, cursor:"pointer", fontSize:isMobile?12:15, fontWeight:900, display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1, flexShrink:0 }}>
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>
      </div>
      </div>

      {detalhe && producao && resultadoProduto && (
        <CommerceDialog className="product-editor-dialog" title="Editar produto" subtitle={detalhe.nome} onClose={() => setDetalheId(null)} footer={<>
          {erroEdicao && <p className="product-edit-error" role="alert">{erroEdicao}</p>}
          <details className="commerce-secondary"><summary>Mais ações</summary><Btn small danger onClick={() => del(detalheId)}>Excluir produto</Btn></details>
          <Btn small outline onClick={() => setDetalheId(null)}>Fechar</Btn>
          <Btn small color={C.success} onClick={salvarEdicao} disabled={salvandoProduto || !editForm.nome.trim() || !!erroEdicao}>{salvandoProduto ? "Salvando…" : "Salvar alterações"}</Btn>
        </>}>
          <div className="product-editor-layout">
          <div className="product-editor-main">
          <CommerceTabs>
            <CommercePanel label="Informações">
              <div className="commerce-stack">
            <Inp label="NOME" value={editForm.nome} onChange={e=>setEditForm({...editForm,nome:e.target.value})} />

            <LinkProdutoMakerWorld valor={editForm.linkMakerworld || ""} desabilitado={salvandoProduto}
              onChange={valor=>setEditForm(f=>({...f,linkMakerworld:valor}))} />

            <section aria-label="Categorias do produto">
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, marginBottom:8 }}>
                <div>
                  <div style={{ color:C.text, fontSize:13, fontWeight:700 }}>Categorias <span style={{ color:C.muted, fontWeight:400 }}>(opcional)</span></div>
                  <div style={{ color:C.muted, fontSize:11, marginTop:3 }}>
                    {editForm.categorias.length ? `${editForm.categorias.length} selecionada${editForm.categorias.length > 1 ? "s" : ""}` : "Nenhuma selecionada"}
                  </div>
                </div>
                {editForm.categorias.length > 0 && (
                  <button type="button" onClick={() => { setCategoriasEditadas(true); setEditForm(f => ({ ...f, categorias:[] })); }}
                    style={{ background:"none", border:"none", color:C.accent2, fontSize:11, cursor:"pointer", padding:4 }}>
                    Limpar
                  </button>
                )}
              </div>
              {categorias.length === 0 ? (
                <div style={{ color:C.muted, fontSize:12, padding:"12px 0" }}>Nenhuma categoria cadastrada. Use o menu Categorias na lista de produtos para criar uma.</div>
              ) : (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,140px),1fr))", gap:8 }}>
                  {categorias.map(c => {
                    const marcada = editForm.categorias.includes(c.nome);
                    return (
                      <button type="button" key={c.id} aria-pressed={marcada}
                        onClick={() => { setCategoriasEditadas(true); setEditForm(f => ({ ...f, categorias: f.categorias.includes(c.nome) ? f.categorias.filter(x => x !== c.nome) : [...f.categorias, c.nome] })); }}
                        style={{ display:"flex", alignItems:"center", gap:9, minWidth:0, textAlign:"left", background:marcada?"rgba(0,212,170,0.12)":C.card, border:"1px solid "+(marcada?C.accent2:C.border), borderRadius:10, padding:"9px 10px", color:marcada?C.accent2:C.text, fontSize:12, fontWeight:marcada?700:500, cursor:"pointer" }}>
                        <span aria-hidden="true" style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:16, height:16, flexShrink:0, borderRadius:5, border:"1px solid "+(marcada?C.accent2:C.muted), background:marcada?C.accent2:"transparent", color:marcada?"#06281f":"transparent", fontSize:12, fontWeight:900 }}>{marcada ? "\u2713" : ""}</span>
                        <span style={{ overflowWrap:"anywhere" }}>{emojiDaCategoria(c.nome)} {c.nome}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>

              <section><h3 style={{fontSize:14, margin:"0 0 10px"}}>Disponibilidade</h3>
            <div style={{ display:"flex", alignItems:"center", gap:10, background:C.card, borderRadius:10, padding:"10px 14px" }}>
              <span style={{ color:C.muted, fontSize:12, fontWeight:700, flex:1 }}>ESTOQUE</span>
              <button onClick={() => ajustarEstoque(detalheId, -1)}
                style={{ width:32, height:32, border:"1px solid "+C.border, background:C.panel, color:C.danger, borderRadius:8, cursor:"pointer", fontSize:20, fontWeight:900, display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>
                −
              </button>
              {editandoEstoqueId === detalheId ? (
                <input
                  type="number" autoFocus value={valorEstoqueEdit}
                  onChange={e=>setValorEstoqueEdit(e.target.value)}
                  onBlur={()=>definirEstoque(detalheId, valorEstoqueEdit)}
                  onKeyDown={e=>{ if(e.key==="Enter") e.currentTarget.blur(); if(e.key==="Escape") setEditandoEstoqueId(null); }}
                  style={inputEstoqueStyle(false)}
                />
              ) : (
                <span className="estoque-editavel" onClick={()=>abrirEdicaoEstoque(detalhe)} title="Clique pra digitar o estoque"
                  style={{ color: (parseInt(detalhe.estoque)||0) > 0 ? C.text : C.danger, fontWeight:900, fontSize:22, minWidth:36, textAlign:"center", cursor:"pointer" }}>
                  {parseInt(detalhe.estoque)||0}<LapisEstoque />
                </span>
              )}
              <button onClick={() => ajustarEstoque(detalheId, +1)}
                style={{ width:32, height:32, border:"1px solid "+C.border, background:C.panel, color:C.success, borderRadius:8, cursor:"pointer", fontSize:20, fontWeight:900, display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>
                +
              </button>
              <Badge color={(parseInt(detalhe.estoque)||0) > 0 ? C.accent2 : C.danger} style={{ marginLeft:4 }}>
                {(parseInt(detalhe.estoque)||0) > 0 ? "Em estoque" : "Esgotado"}
              </Badge>
            </div>

                <p className="commerce-hint">As alterações de estoque são salvas na hora.</p>
              </section>
              </div>
            </CommercePanel>
            <CommercePanel label="Produção">
              {carregandoInsumos ? <p className="commerce-hint">Carregando filamentos e impressoras…</p> : <ProdutoProducaoEditor produto={detalhe} form={producao} onChange={setProducao} filamentos={filamentos} impressoras={impressoras} coresCustom={coresCustom} />}
            </CommercePanel>
            <CommercePanel label="Mídia e arquivos">
              <div className="commerce-stack">
            <div>
              <div style={{ color:C.muted, fontSize:12, fontWeight:600, letterSpacing:1, marginBottom:6 }}>FOTOS E VÍDEOS</div>
              <FotoUploader
                fotos={editForm.fotos.map(f => ({ id: f.path, url: f.url, tipo: f.tipo }))}
                onAdicionar={adicionarFotos}
                onRemover={removerFoto}
                onReordenar={reordenarFotos}
                processando={enviandoFotos}
              />
              <ImportarFotosMakerWorld produtoId={detalheId} onImportadas={registrarFotosImportadas} disabled={enviandoFotos} linkInicial={editForm.linkMakerworld || ""} fotosAtuais={editForm.fotos} />
              {editForm.fotos.length === 0 && editForm.fotoBase64 && (
                <div style={{ color:C.muted, fontSize:11, marginTop:6 }}>Capa atual, extraída do arquivo 3D <img src={editForm.fotoBase64} alt="foto atual" style={{ width:32, height:32, objectFit:"cover", borderRadius:6, verticalAlign:"middle", marginLeft:6, border:"1px solid "+C.border }} /> — costuma ficar com pouca resolução (borrada). Adicione uma foto real acima pra ficar nítida no site e nos orçamentos.</div>
              )}
            </div>

              <ArquivoCard produto={detalhe} onAtualizar={()=>carregar(true)} />
              {detalhe.visualizacao3d && (
                <Visualizador3D modelo={visualizacao3dDoItem(detalhe)} nome={detalhe.nome} />
              )}
              </div>
            </CommercePanel>

          </CommerceTabs>
          </div>
          <ProdutoFinanceiro resultado={resultadoProduto} preco={editForm.preco} onPrecoChange={preco=>setEditForm(f=>({...f,preco}))}/>
          </div>
        </CommerceDialog>
      )}
    </div>
  );
}
